import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../../services/api';
import { handleApiError } from '../../services/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-secondary-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="card">
            <div className="card-body text-center space-y-4">
              <div className="text-4xl">🔗</div>
              <p className="text-secondary-700 text-sm">Invalid reset link. No token was provided.</p>
              <Link to="/forgot-password" className="block text-sm font-medium text-primary-600 hover:text-primary-500">
                Request a new reset link
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors(prev => ({ ...prev, [e.target.name]: undefined }));
    setApiError('');
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError('');

    try {
      await apiService.resetPassword(token, formData.password);
      setSuccess(true);
    } catch (err) {
      const msg = handleApiError(err);
      // Surface a more helpful message for expired/used tokens
      if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('already been used')) {
        setApiError('This reset link has expired or has already been used. Request a new one.');
      } else {
        setApiError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <Link to="/" aria-label="OnlyKickers home" className="inline-block mb-5">
            <span className="font-display text-2xl tracking-tight leading-none text-secondary-900">
              ONLY<span className="text-gold-700">KICKERS</span>
            </span>
          </Link>
          <h2 className="font-display uppercase text-3xl sm:text-4xl leading-none text-ink-900">
            New Password
          </h2>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card">
          <div className="card-body">
            {success ? (
              <div className="text-center space-y-4">
                <div className="text-4xl">✅</div>
                <p className="text-secondary-700 text-sm">
                  Your password has been reset successfully.
                </p>
                <Link to="/login">
                  <button className="btn-primary w-full mt-2">
                    Sign in with new password
                  </button>
                </Link>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                {apiError && (
                  <div className="alert-danger">
                    {apiError}
                    {(apiError.includes('expired') || apiError.includes('already been used')) && (
                      <div className="mt-2">
                        <Link to="/forgot-password" className="font-medium underline">
                          Request a new reset link
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="password" className="form-label">
                    New password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    className={`form-input ${errors.password ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                    placeholder="At least 8 characters"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  {errors.password && (
                    <p className="form-error">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirm new password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    className={`form-input ${errors.confirmPassword ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                    placeholder="Repeat your new password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  {errors.confirmPassword && (
                    <p className="form-error">{errors.confirmPassword}</p>
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="loading-spinner w-4 h-4 mr-2"></div>
                        Resetting...
                      </div>
                    ) : (
                      'Reset password'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
